import { Download } from '@mui/icons-material';
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';

import Button from 'domains/core/components/Button';

export type Props = {
    isOpen: boolean;
    onClose: () => void;
    onDownloadAll: () => void;
    onDownloadFile: (filename: string) => void;
    filenames: string[];
    isDownloading: boolean;
    currentFilename: string | null;
};

const DashboardDownloadDialog = ({
    isOpen,
    onClose,
    onDownloadAll,
    onDownloadFile,
    filenames,
    isDownloading,
    currentFilename,
}: Props) => (
    <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="dashboard-download-dialog-title"
        data-testid="dashboard-download-dialog"
    >
        <DialogTitle id="dashboard-download-dialog-title">
            <Stack direction="row" alignItems="center" spacing={1}>
                <Download fontSize="small" />
                <Typography variant="h6">Download Reports</Typography>
            </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
            <List disablePadding>
                {filenames.map((filename, index) => {
                    const isActive = isDownloading && currentFilename === filename;
                    return (
                        <div key={filename}>
                            <ListItem
                                data-testid={`download-file-row-${filename}`}
                                secondaryAction={
                                    <Button
                                        variant="text"
                                        size="small"
                                        disabled={isDownloading}
                                        onClick={() => onDownloadFile(filename)}
                                        aria-label={`Download ${filename}`}
                                        data-testid={`download-file-button-${filename}`}
                                        startIcon={
                                            isActive ? (
                                                <CircularProgress
                                                    size={14}
                                                    aria-label={`Downloading ${filename}`}
                                                    role="progressbar"
                                                />
                                            ) : (
                                                <Download fontSize="small" />
                                            )
                                        }
                                    >
                                        {isActive ? 'Downloading…' : 'Download'}
                                    </Button>
                                }
                            >
                                <ListItemText
                                    primary={filename}
                                    primaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-all' } }}
                                />
                            </ListItem>
                            {index < filenames.length - 1 && <Divider component="li" />}
                        </div>
                    );
                })}
            </List>
        </DialogContent>
        <DialogActions>
            <Stack direction="row" justifyContent="flex-end" spacing={1} width="100%">
                <Button variant="text" onClick={onClose} disabled={isDownloading} data-testid="download-dialog-cancel">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isDownloading}
                    onClick={onDownloadAll}
                    startIcon={
                        isDownloading && !currentFilename ? (
                            <CircularProgress size={14} role="progressbar" aria-label="Downloading all reports" />
                        ) : (
                            <Download fontSize="small" />
                        )
                    }
                    data-testid="download-dialog-all"
                    aria-label="Download all reports as ZIP"
                >
                    {isDownloading && !currentFilename ? 'Downloading…' : 'Download All (.zip)'}
                </Button>
            </Stack>
        </DialogActions>
    </Dialog>
);

export default DashboardDownloadDialog;
